import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UserCircle, Briefcase, FileSignature, Receipt, Bell, AlertTriangle, Calendar, Inbox } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useCrm } from '../store';
import { useStructureFilter, STRUCTURE_LABELS } from '../structureContext';
import { useMesPrestationsNumeros } from '../intervenants';
import { PageTitle } from '../ui';
import { STATUT_COLORS, DEVIS_STATUT_COLORS, FACTURE_STATUT_COLORS } from '../types';
import { formatEUR } from '../utils';

export default function MesDossiers() {
  const { user } = useAuth();
  const { matches, filter } = useStructureFilter();
  const data = useCrm();
  const { numeros: mesNumeros } = useMesPrestationsNumeros(user?.id);

  const numeroSet = useMemo(() => new Set(mesNumeros), [mesNumeros]);

  const mesPrestations = useMemo(() => {
    return data.prest
      .filter((p) => matches(p.structure))
      .filter((p) => numeroSet.has(p.id))
      .sort((a, b) => (b.dateEvent || '').localeCompare(a.dateEvent || ''));
  }, [data.prest, matches, numeroSet]);

  const mesDevis = useMemo(() => {
    return (data.devis || [])
      .filter((d) => matches(d.structure))
      .filter((d) => d.prestationId && numeroSet.has(d.prestationId))
      .sort((a, b) => (b.dateEmission || '').localeCompare(a.dateEmission || ''));
  }, [data.devis, matches, numeroSet]);

  const mesFactures = useMemo(() => {
    return (data.factures || [])
      .filter((f) => matches(f.structure))
      .filter((f) => f.prestationId && numeroSet.has(f.prestationId))
      .sort((a, b) => (b.dateEmission || '').localeCompare(a.dateEmission || ''));
  }, [data.factures, matches, numeroSet]);

  const aRelancer = useMemo(() => {
    const daysSince = (iso?: string) => {
      if (!iso) return 0;
      return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    };
    return mesPrestations.filter(
      (p) =>
        /relance|propal a faire/i.test(p.statut) ||
        (p.statut === 'Demande de Rdv' && daysSince(p.updatedAt || p.dateCreation) >= 3)
    );
  }, [mesPrestations]);

  const facturesImpayees = useMemo(() => {
    const today = new Date();
    return mesFactures.filter((f) => {
      if (['Payée', 'Annulée', 'Brouillon'].includes(f.statut)) return false;
      const ttc = f.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0) * (1 - (f.remisePct || 0) / 100);
      const reste = Math.max(0, ttc - (f.montantPaye || 0) - (f.acompteVerse || 0));
      return reste > 0.01 && f.dateEcheance && new Date(f.dateEcheance) < today;
    });
  }, [mesFactures]);

  const evenementsAVenir = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return mesPrestations
      .filter((p) => p.dateEvent && p.dateEvent >= today && p.statut === 'Signé')
      .sort((a, b) => a.dateEvent.localeCompare(b.dateEvent))
      .slice(0, 8);
  }, [mesPrestations]);

  const ca = useMemo(() => {
    return mesFactures
      .filter((f) => f.statut === 'Payée')
      .reduce((s, f) => {
        const ttc = f.lignes.reduce((ss, l) => ss + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0) * (1 - (f.remisePct || 0) / 100);
        return s + ttc;
      }, 0);
  }, [mesFactures]);

  return (
    <div className="space-y-6">
      <PageTitle
        icon={<UserCircle size={20} strokeWidth={1.5} className="text-amber-400" />}
        title="Mes dossiers"
        subtitle={
          mesNumeros.length === 0
            ? 'Aucun dossier ne t\u2019est encore attribué'
            : `${mesPrestations.length} prestation${mesPrestations.length > 1 ? 's' : ''} sur ${filter === 'all' ? 'toutes structures' : STRUCTURE_LABELS[filter]}`
        }
      />

      {/* KPIs perso */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI icon={<Briefcase size={14} />} label="Prestations" value={mesPrestations.length} tone="amber" />
        <KPI icon={<FileSignature size={14} />} label="Devis ouverts" value={mesDevis.filter((d) => ['Brouillon', 'Envoyé', 'Vu', 'Relance'].includes(d.statut)).length} tone="violet" />
        <KPI icon={<Bell size={14} />} label="À relancer" value={aRelancer.length} tone={aRelancer.length > 0 ? 'red' : 'slate'} />
        <KPI icon={<Receipt size={14} />} label="CA encaissé" value={formatEUR(ca)} tone="emerald" small />
      </div>

      {mesNumeros.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-10 text-center space-y-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-800/60 text-amber-400">
            <Inbox size={22} strokeWidth={1.5} />
          </div>
          <div className="space-y-2">
            <p className="text-slate-200 font-medium">Aucun dossier ne t\u2019est encore attribué</p>
            <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
              Les dossiers apparaissent ici dès qu\u2019un administrateur t\u2019ajoute comme intervenant\u00A0sur une prestation. Contacte ta direction pour être assigné·e à un dossier.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Événements à venir */}
          <Bloc icon={<Calendar size={15} />} title="Mes événements à venir" count={evenementsAVenir.length}>
            {evenementsAVenir.length === 0 ? (
              <Empty>Aucun événement signé à venir</Empty>
            ) : (
              <ul className="space-y-1.5">
                {evenementsAVenir.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 text-sm bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
                    <span className="text-amber-400 text-xs font-mono w-20 shrink-0">{formatShortDate(p.dateEvent)}</span>
                    <span className="flex-1 truncate text-slate-200">{p.client}</span>
                    <span className="text-xs text-slate-500 truncate">{p.lieu || '—'}</span>
                  </li>
                ))}
              </ul>
            )}
          </Bloc>

          {/* À relancer */}
          <Bloc icon={<Bell size={15} />} title="Prestations à relancer" count={aRelancer.length} danger={aRelancer.length > 0}>
            {aRelancer.length === 0 ? (
              <Empty>Tout est à jour 👌</Empty>
            ) : (
              <ul className="space-y-1.5">
                {aRelancer.slice(0, 8).map((p) => (
                  <li key={p.id} className="flex items-center gap-2 text-sm bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUT_COLORS[p.statut] || 'bg-slate-700/30 text-slate-300 border-slate-600/40'}`}>
                      {p.statut}
                    </span>
                    <span className="flex-1 truncate text-slate-200">{p.client}</span>
                    <span className="text-xs text-slate-500">{formatShortDate(p.dateEvent)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Bloc>

          {/* Devis en cours */}
          <Bloc icon={<FileSignature size={15} />} title="Mes devis en cours" count={mesDevis.length}>
            {mesDevis.length === 0 ? (
              <Empty>Aucun devis sur tes dossiers</Empty>
            ) : (
              <ul className="space-y-1.5">
                {mesDevis.slice(0, 8).map((d) => (
                  <li key={d.id} className="flex items-center gap-2 text-sm bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${DEVIS_STATUT_COLORS[d.statut]}`}>{d.statut}</span>
                    <span className="font-mono text-xs text-slate-500 w-24 truncate shrink-0">{d.numero}</span>
                    <span className="flex-1 truncate text-slate-200">{d.clientNom}</span>
                  </li>
                ))}
              </ul>
            )}
          </Bloc>

          {/* Factures impayées */}
          <Bloc icon={<AlertTriangle size={15} />} title="Factures impayées en retard" count={facturesImpayees.length} danger={facturesImpayees.length > 0}>
            {facturesImpayees.length === 0 ? (
              <Empty>Aucun retard de paiement</Empty>
            ) : (
              <ul className="space-y-1.5">
                {facturesImpayees.map((f) => {
                  const ttc = f.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0) * (1 - (f.remisePct || 0) / 100);
                  const reste = Math.max(0, ttc - (f.montantPaye || 0) - (f.acompteVerse || 0));
                  return (
                    <li key={f.id} className="flex items-center gap-2 text-sm bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${FACTURE_STATUT_COLORS[f.statut]}`}>{f.statut}</span>
                      <span className="font-mono text-xs text-slate-500 w-24 truncate shrink-0">{f.numero}</span>
                      <span className="flex-1 truncate text-slate-200">{f.clientNom}</span>
                      <span className="text-xs text-red-400 font-medium">{formatEUR(reste)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Bloc>
        </div>
      )}

      {mesPrestations.length > 0 && (
        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-slate-200 flex items-center gap-2">
              <Briefcase size={15} strokeWidth={1.5} className="text-amber-400" />
              Toutes mes prestations
            </h3>
            <Link to="../prestations" className="text-xs text-amber-400 hover:text-amber-300">Voir toutes les prestations →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] text-slate-500 uppercase tracking-wider">
                <tr className="border-b border-slate-800">
                  <th className="text-left py-2 px-2">N°</th>
                  <th className="text-left py-2 px-2">Client</th>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Lieu</th>
                  <th className="text-left py-2 px-2">Statut</th>
                  <th className="text-right py-2 px-2">Devis TTC</th>
                </tr>
              </thead>
              <tbody>
                {mesPrestations.map((p) => (
                  <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="py-2 px-2 font-mono text-xs text-slate-500">{p.id}</td>
                    <td className="py-2 px-2 text-slate-200 truncate max-w-[200px]">{p.client}</td>
                    <td className="py-2 px-2 text-slate-400 text-xs">{formatShortDate(p.dateEvent)}</td>
                    <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[200px]">{p.lieu || '—'}</td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUT_COLORS[p.statut] || 'bg-slate-700/30 text-slate-300 border-slate-600/40'}`}>
                        {p.statut}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right text-slate-300 text-xs">{p.propTTC ? formatEUR(p.propTTC) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ icon, label, value, tone, small }: { icon: React.ReactNode; label: string; value: number | string; tone: 'amber' | 'violet' | 'red' | 'emerald' | 'slate'; small?: boolean }) {
  const colors: Record<string, string> = {
    amber: 'text-amber-400',
    violet: 'text-violet-400',
    red: 'text-red-400',
    emerald: 'text-emerald-400',
    slate: 'text-slate-300',
  };
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
        <span className={colors[tone]}>{icon}</span>
        {label}
      </div>
      <div className={`${small ? 'text-xl' : 'text-2xl'} font-heading text-slate-100`}>{value}</div>
    </div>
  );
}

function Bloc({ icon, title, count, danger, children }: { icon: React.ReactNode; title: string; count: number; danger?: boolean; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-slate-200 flex items-center gap-2">
          <span className={danger ? 'text-red-400' : 'text-amber-400'}>{icon}</span>
          {title}
        </h3>
        <span className={`text-xs px-2 py-0.5 rounded-full ${danger ? 'bg-red-500/20 text-red-400' : 'bg-slate-800 text-slate-400'}`}>{count}</span>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-slate-500 italic py-2">{children}</div>;
}

function formatShortDate(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
}
